package com.techspiration.webuserapp.site

import androidx.compose.runtime.Composable
import com.techspiration.core.contract.WebStoreTarget
import com.techspiration.webuserapp.HomeRoute
import com.techspiration.webuserapp.PrivacyRoute
import com.techspiration.webuserapp.TermsRoute
import com.techspiration.webuserapp.UserNavigator
import com.techspiration.webuserapp.api.WebTelemetry
import com.techspiration.webuserapp.path
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Footer
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Herkese açık tanıtım sitesinin ortak "chrome" bileşenleri (nav/footer/kart/bölüm sarmalayıcı).
 * Sayfa İÇERİĞİ burada yok — [com.techspiration.webuserapp.home.HomeScreen] ve diğer ekranlar
 * bu bileşenleri kullanarak sayfa gövdesini doldurur.
 *
 * Tüm class isimleri `webUserApp/src/jsMain/resources/index.html` içindeki global CSS tasarım
 * sistemiyle birebir eşleşir — yeni class eklemeden önce oradaki tanımlı isimleri kullan.
 */

/**
 * Üst navigasyon çubuğu (`.site-nav`) — solda logo (Ana Sayfa'ya döner), sağda sayfa-içi
 * çapa (anchor) linkleri + "Ücretsiz İndir" birincil buton.
 *
 * @param navigator logo tıklamasında [HomeRoute]'a dönmek için kullanılan SPA navigator'ı.
 */
@Composable
fun SiteNav(navigator: UserNavigator) {
    Header(attrs = { classes("site-nav") }) {
        Div(attrs = { classes("nav-inner") }) {
            Div(
                attrs = {
                    classes("nav-logo")
                    onClick { navigator.goto(HomeRoute) }
                },
            ) {
                Img(src = "/app-icon.png", alt = "Atama Bildirim logosu")
                Span { Text("Atama Bildirim") }
            }
            Nav(attrs = { classes("nav-links") }) {
                A(href = "#ozellikler", attrs = { classes("nav-link") }) { Text("Özellikler") }
                A(href = "#nasil-calisir", attrs = { classes("nav-link") }) { Text("Nasıl Çalışır") }
                A(href = "#indir", attrs = { classes("nav-link") }) { Text("İndir") }
                A(href = "#indir", attrs = { classes("btn", "btn-primary") }) { Text("Ücretsiz İndir") }
            }
        }
    }
}

/**
 * Mağaza yönlendirme butonları (`.store-badges`) — App Store ve Google Play sayfalarına
 * yeni sekmede açılan linkler. Görsel rozet yok; `.btn` stilinde metin butonları kullanılır.
 *
 * Tıklamalar telemetriye yazılır ([WebTelemetry.trackStoreClick]) ama navigasyon ASLA
 * beklemez: `preventDefault` çağrılmaz, gönderim ateşle-unut'tur. Ağ yavaşsa bile tarayıcı
 * mağaza sekmesini anında açar.
 */
@Composable
fun StoreBadges() {
    Div(attrs = { classes("store-badges") }) {
        A(
            href = "https://apps.apple.com/tr/app/atama-bildirim-kamu-i-lanlar%C4%B1/id6780600221",
            attrs = {
                classes("btn", "btn-primary")
                attr("target", "_blank")
                attr("rel", "noopener")
                onClick { WebTelemetry.trackStoreClick(WebStoreTarget.APP_STORE) }
            },
        ) { Text("App Store") }
        A(
            href = "https://play.google.com/store/apps/details?id=com.techspiration.kamu&hl=tr",
            attrs = {
                classes("btn", "btn-ghost")
                attr("target", "_blank")
                attr("rel", "noopener")
                onClick { WebTelemetry.trackStoreClick(WebStoreTarget.PLAY_STORE) }
            },
        ) { Text("Google Play") }
    }
}

/**
 * Sayfa altbilgisi (`.site-footer`) — solda logo + telif metni, sağda yasal/iletişim linkleri.
 * Gizlilik/Şartlar linkleri hem gerçek `href` taşır (doğrudan URL veya sayfa yenilemesiyle de
 * doğru rotaya düşer) hem de [navigator] üzerinden SPA içi anlık geçiş yapar.
 *
 * @param navigator Gizlilik Politikası / Kullanım Şartları linklerinin SPA içi geçişi için.
 */
@Composable
fun SiteFooter(navigator: UserNavigator) {
    Footer(attrs = { classes("site-footer") }) {
        Div(attrs = { classes("footer-inner") }) {
            Div {
                Div(attrs = { classes("nav-logo") }) {
                    Img(src = "/app-icon.png", alt = "Atama Bildirim logosu")
                    Span { Text("Atama Bildirim") }
                }
                P(attrs = { classes("t-label") }) { Text("© 2026 Atama Bildirim · Techspiration") }
            }
            Div(attrs = { classes("footer-links") }) {
                A(
                    href = PrivacyRoute.path(),
                    attrs = {
                        classes("footer-link")
                        onClick {
                            it.preventDefault()
                            navigator.goto(PrivacyRoute)
                        }
                    },
                ) { Text("Gizlilik Politikası") }
                A(
                    href = TermsRoute.path(),
                    attrs = {
                        classes("footer-link")
                        onClick {
                            it.preventDefault()
                            navigator.goto(TermsRoute)
                        }
                    },
                ) { Text("Kullanım Şartları") }
                A(
                    href = "mailto:kamuapp@techspiration.me",
                    attrs = { classes("footer-link") },
                ) { Text("İletişim") }
            }
        }
    }
}

/**
 * Bir landing bölümünü (`.section > .container`) sarmalar. `id` verilirse sayfa-içi çapa
 * (`#ozellikler` gibi) hedefi olur; `extraClass` ile ek CSS class'ları (ör. hero arka planı)
 * `.section` elemanına eklenir.
 *
 * @param id sayfa-içi anchor kaydırma hedefi (opsiyonel).
 * @param extraClass `.section` elemanına eklenecek, boşlukla ayrılmış ek class isim(ler)i.
 * @param content bölüm içeriği — `.container` içine yerleştirilir.
 */
@Composable
fun SectionContainer(
    id: String? = null,
    extraClass: String = "",
    content: @Composable () -> Unit,
) {
    Section(
        attrs = {
            classes("section")
            val extra = extraClass.split(" ").filter { it.isNotBlank() }
            if (extra.isNotEmpty()) {
                classes(*extra.toTypedArray())
            }
            if (id != null) {
                id(id)
            }
        },
    ) {
        Div(attrs = { classes("container") }) {
            content()
        }
    }
}

/**
 * Tek bir özellik kartı (`.card`) — ikon rozeti (`.feature-icon`), başlık (`.t-title`) ve
 * açıklama (`.t-body`). `.card-grid` içine dizilecek şekilde tasarlandı.
 *
 * @param icon kart üstünde gösterilecek emoji/simge metni.
 * @param title kart başlığı.
 * @param body kart açıklama metni.
 */
@Composable
fun FeatureCard(icon: String, title: String, body: String) {
    Div(attrs = { classes("card") }) {
        Div(attrs = { classes("feature-icon") }) { Text(icon) }
        H3(attrs = { classes("t-title") }) { Text(title) }
        P(attrs = { classes("t-body") }) { Text(body) }
    }
}
